import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatInteger, numberLocale } from '@/lib/language';
import { useChapterOpen } from '@/hooks/optimizationScore/chapterOpenPreference';
import type { LiveFeedStatus } from '@/hooks/useLiveFeedStatus';

/**
 * Kapitel "Bei Google" (Spec `docs/superpowers/specs/2026-09-16-journeybar-statusfluss-design.md`,
 * Abschnitt 6). Ziel des Live-Chips der Fortschrittsleiter (`/#live`).
 *
 * WAS DIESES KAPITEL NICHT BEHAUPTEN DARF, und das ist seine wichtigste Regel:
 * einen Zeitpunkt, zu dem Google unseren Supplemental Feed gelesen oder
 * verarbeitet hat. Den gibt es in unseren Daten nicht. Belegbar sind genau
 * zwei Vorgaenge, und beide sind UNSERE: `supplemental_feeds.last_upload_at`
 * (wir haben hochgeladen) und `feeds.last_synced_at` (wir haben die Merchant
 * API gelesen). Die Beschriftung nennt deshalb nur diese beiden, nie "von
 * Google gelesen". Ebenso erscheinen hier KEINE Ablehnungen bei Google:
 * `products.shopping_ads_disapproved_at` wird nur fuer die fuenf
 * Rollout-Konten gezogen, NULL heisst dort "nie geprueft" und nicht "in
 * Ordnung". Ausnahme seit WP5 (A2O-03): meldet Google Ablehnungen
 * (`disapprovedCount > 0`), steht darunter ein Satz dazu, sonst widerspricht
 * die Zusammenfassung der Liste oben. 0 oder fehlend bleibt stumm.
 *
 * Aufbau wie `AdsChapterRow`: Kopfzeile mit Kapitelnummer und
 * Zusammenfassung, aufklappbarer Rumpf, hoechstens EINE Handlungszeile. Die
 * Seite reicht die fertige Zeile als Knoten an `FeedCheckChapters`.
 */
export interface LiveChapterRowProps {
  chapterIndex: number;
  chapterTotal: number;
  feedId: string | null;
  live: LiveFeedStatus;
  /** Zur Werkbank, Reiter "wartend" mit dem Filter `scope=awaiting_upload`. */
  onReview: () => void;
  /** Oeffnet den Upload-Dialog DER SEITE (kein zweiter `useUploadJob`). */
  onUpload: () => void;
  uploadBusy?: boolean;
  /**
   * WP5 A2O-03: von Google fuer Shopping-Anzeigen abgelehnte Produkte dieses
   * Feeds. Nur > 0 ist eine Aussage (der Abruf deckt nur die Rollout-Konten
   * ab, 0 oder fehlend heisst "nicht geprueft" oder "nichts"), deshalb
   * erscheint der Satz nur dann.
   */
  disapprovedCount?: number;
  /**
   * Aufklapp-Bitte von aussen (Plan-Entscheidung E4): der Anker `#live` und
   * die Handlungskarte bitten ueber diesen Wert, nicht ueber eine zweite
   * `useChapterOpen`-Instanz -- die haelt ihren Zustand je Instanz und saehe
   * die hier gehaltene nicht.
   */
  openRequest?: { chapter: 'ads' | 'live'; tick: number } | null;
}

/**
 * Ein Zeitstempel als Tagesdatum der UI-Sprache.
 *
 * Anders als `AdsChapterBody.formatDay` bekommt diese Funktion volle
 * ISO-Zeitstempel mit Uhrzeit (`last_upload_at`, `last_synced_at` sind
 * `timestamptz`), nicht reine Datumszeichenketten -- das dortige
 * `+ 'T00:00:00'` waere hier falsch und ergaebe "Invalid Date".
 */
function formatTimestamp(iso: string, language: string | undefined): string {
  return new Date(iso).toLocaleDateString(numberLocale(language));
}

export function LiveChapterRow({
  chapterIndex,
  chapterTotal,
  feedId,
  live,
  onReview,
  onUpload,
  uploadBusy = false,
  disapprovedCount = 0,
  openRequest,
}: LiveChapterRowProps) {
  const { t, i18n } = useTranslation('optimization');
  const lang = i18n?.language;
  const [open, setOpen] = useChapterOpen(feedId, 'live');
  const contentId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const letzterTick = useRef<number | null>(null);

  const total = live.stats.total;
  const liveCount = live.stats.uploaded + live.stats.needsReupload;
  const ueberdeckung = total > 0 ? Math.round((liveCount / total) * 100) : 0;
  const hatZeile = !!live.row;

  /**
   * "Wir wissen es noch nicht" ist ein eigener Zustand, und das ist der
   * wichtigste Zweig dieser Komponente.
   *
   * `live.row` ist waehrend der laufenden Abfrage `null`, und `live.isError`
   * ist dabei `false` -- ohne diesen Riegel zeigte die Kopfzeile in genau
   * diesem Fenster den Leerzustand ("Noch kein Live-Feed"), und zwar auf dem
   * Haupt-Dashboard jedes Kunden, der laengst einen hat. Eine Falschaussage
   * fuer ein paar hundert Millisekunden je Seitenaufbau und je Feedwechsel ist
   * immer noch eine Falschaussage; das Ads-Kapitel loest denselben Fall an
   * derselben Stelle ueber seinen Skeleton.
   *
   * `statsFeedId !== feedId` gehoert dazu: `useProductStats` haelt beim
   * Feedwechsel per `placeholderData` die Zahlen des VORHERIGEN Feeds und
   * meldet dabei nicht mehr "laedt". In diesem Fenster zeigte die
   * Handlungszeile die Menge eines fremden Feeds -- derselbe Riegel, den
   * `useJourney` und `WorkbenchUploadAction` benutzen.
   */
  const laedt = live.isLoading || live.statsFeedId !== feedId;

  // Ein Aufklapper ohne Rumpf ist eine Sackgasse: ohne Supplemental-Zeile und
  // im Fehlerfall gibt es keinen, nur den Satz in der Kopfzeile. Waehrend des
  // Ladens ebenso: ein Rumpf mit Platzhalter-Nullen behauptete Zahlen.
  const hatRumpf = !laedt && hatZeile && !live.isError;

  useEffect(() => {
    if (!openRequest || openRequest.chapter !== 'live') return;
    if (letzterTick.current === openRequest.tick) return;
    letzterTick.current = openRequest.tick;
    if (hatRumpf) setOpen(true);
    // `scrollIntoView` auch ohne Rumpf: der Leerzustand ist die Antwort auf
    // "warum ist nichts live", und wer dem Anker folgt, soll ihn sehen.
    //
    // Der optionale Aufruf ist kein Zierrat: jsdom implementiert
    // `scrollIntoView` nicht, und ein Wurf hier nahm das Aufklappen mit -- die
    // FUNKTIONALE Haelfte dieser Bitte. Das Scrollen ist die Bequemlichkeit,
    // das Aufklappen die Aussage, und die darf nicht an der Bequemlichkeit
    // haengen.
    rootRef.current?.scrollIntoView?.({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `setOpen` ist pro Feed stabil, `hatRumpf` soll die Bitte nicht erneut ausloesen
  }, [openRequest]);

  /**
   * GENAU EINE Handlungszeile, in der Vorfahrt aus Spec 6: was auf die
   * Pruefung wartet, steht vor dem, was auf den Upload wartet. Zwei Zeilen
   * gleichzeitig waeren zwei naechste Schritte an derselben Stelle, und das
   * ist genau das, was die Leiter abschafft.
   */
  const handlung: 'review' | 'upload' | null =
    laedt
      ? null
      : live.stats.pendingReviewAwaitingUpload > 0
        ? 'review'
        : live.stats.approvedAwaitingUpload > 0
          ? 'upload'
          : null;

  const zusammenfassung = laedt
    ? t('feedCheck.live.loading')
    : live.isError
      ? t('feedCheck.live.error')
      : !hatZeile
        ? t('feedCheck.live.empty')
        : t('feedCheck.live.summaryLine', {
            count: liveCount,
            live: formatInteger(liveCount, lang),
            total: formatInteger(total, lang),
            share: ueberdeckung,
          });

  return (
    <div ref={rootRef} data-testid={`chapter-live-${laedt ? 'loading' : live.isError ? 'error' : hatZeile ? 'ready' : 'empty'}`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-heading font-bold">
            {t('feedCheck.live.label', { index: chapterIndex, total: chapterTotal })}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{zusammenfassung}</p>
          {!laedt && !live.isError && hatZeile && disapprovedCount > 0 && (
            <p data-testid="live-disapproved-note" className="mt-1 text-xs text-destructive-ink">
              {t('feedCheck.live.disapprovedNote', {
                count: disapprovedCount,
                formatted: formatInteger(disapprovedCount, lang),
              })}
            </p>
          )}
        </div>
        {hatRumpf && (
          <button
            type="button"
            className="print:hidden shrink-0 text-sm font-medium text-primary hover:underline"
            aria-expanded={open}
            aria-controls={open ? contentId : undefined}
            onClick={() => setOpen(!open)}
          >
            {open ? t('feedCheck.live.close') : t('feedCheck.live.open')}
          </button>
        )}
      </div>

      {hatRumpf && open && live.row && (
        <div id={contentId} className="flex flex-col gap-5">
          <div className="grid gap-3 sm:grid-cols-2">
            {/* Links: was der Haendler hat und wann wir es gelesen haben. */}
            <div data-testid="live-primary" className="flex flex-col gap-1.5 rounded-md bg-muted/60 px-4 py-3.5">
              <span className="text-xs text-muted-foreground">{t('feedCheck.live.primary.label')}</span>
              <span className="font-heading text-2xl font-bold leading-none tabular-nums">
                {formatInteger(total, lang)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('feedCheck.live.primary.products', { count: total })}
              </span>
              <span className="text-xs text-muted-foreground">
                {live.lastSyncedAt
                  ? t('feedCheck.live.primary.syncedAt', { date: formatTimestamp(live.lastSyncedAt, lang) })
                  : t('feedCheck.live.primary.neverSynced')}
              </span>
            </div>

            {/* Rechts: was WIR hochgeladen haben. */}
            <div data-testid="live-supplemental" className="flex flex-col gap-1.5 rounded-md bg-muted/60 px-4 py-3.5">
              <span className="text-xs text-muted-foreground">{t('feedCheck.live.supplemental.label')}</span>
              <span className="font-heading text-2xl font-bold leading-none tabular-nums">
                {formatInteger(liveCount, lang)}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('feedCheck.live.supplemental.live', { count: liveCount, share: ueberdeckung })}
              </span>
              <span className="text-xs text-muted-foreground">
                {live.row.last_upload_at
                  ? t('feedCheck.live.supplemental.uploadedAt', {
                      date: formatTimestamp(live.row.last_upload_at, lang),
                    })
                  : t('feedCheck.live.supplemental.neverUploaded')}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('feedCheck.live.supplemental.status', { status: live.row.status })}
              </span>
            </div>
          </div>

          {handlung === 'review' && (
            <div data-testid="live-action-review" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 px-4 py-3">
              <p className="text-sm">
                {t('feedCheck.live.action.reviewLine', {
                  count: live.stats.pendingReviewAwaitingUpload,
                  formatted: formatInteger(live.stats.pendingReviewAwaitingUpload, lang),
                })}
              </p>
              <Button variant="outline" size="sm" className="print:hidden shrink-0" onClick={onReview}>
                {t('feedCheck.live.action.reviewCta')}
              </Button>
            </div>
          )}

          {handlung === 'upload' && (
            <div data-testid="live-action-upload" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 px-4 py-3">
              <p className="text-sm">
                {t('feedCheck.live.action.uploadLine', {
                  count: live.stats.approvedAwaitingUpload,
                  formatted: formatInteger(live.stats.approvedAwaitingUpload, lang),
                })}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="print:hidden shrink-0"
                onClick={onUpload}
                disabled={uploadBusy}
              >
                {t('feedCheck.live.action.uploadCta')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
